import type { Editor as TiptapEditor } from '@tiptap/core';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Template, useTemplate, useUpdateTemplate } from '@/entities/template';
import { StandaloneEditor } from '@/features/document-editor';
import { isApiError } from '@/shared/api';
import { FormError } from '@/shared/forms';
import {
  Button,
  Dialog,
  DialogActions,
  ErrorState,
  IconButton,
  Icons,
  LoadingState,
  TextAreaField,
  TextField,
} from '@/shared/ui';

import {
  applyBoardTemplate,
  BOARD_NAME_MAX,
  boardTemplateProblems,
  type BoardTemplateForm,
  COLUMN_NAME_MAX,
  DESCRIPTION_MAX,
  freshRef,
  readBoardTemplate,
  TASK_TITLE_MAX,
} from './board-template';
import * as db from './database-template';
import styles from './Templates.module.css';

type Tr = (key: string, options?: Record<string, unknown>) => string;

const useTr = (): Tr => {
  const { t } = useTranslation();
  return (key, options) =>
    (t as unknown as (k: string, o?: Record<string, unknown>) => string)(
      `templates.editor.${key}`,
      options,
    );
};

/** What the server said about the name (taken, ...), in words. */
function useNameError(error: unknown): string | undefined {
  const { t } = useTranslation();
  const code = isApiError(error) ? error.fieldErrors['name']?.[0] : undefined;
  return code ? t(`validation.${code}`, { defaultValue: t('validation.INVALID') }) : undefined;
}

function MoveButtons({
  index,
  count,
  onMove,
}: {
  index: number;
  count: number;
  onMove: (by: -1 | 1) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <IconButton
        label={t('templates.edit.up')}
        icon={<Icons.arrowUp />}
        disabled={index === 0}
        onClick={() => {
          onMove(-1);
        }}
      />
      <IconButton
        label={t('templates.edit.down')}
        icon={<Icons.arrowDown />}
        disabled={index === count - 1}
        onClick={() => {
          onMove(1);
        }}
      />
    </>
  );
}

/** The frame every content editor shares: load, name, save / cancel, server errors. */
function EditorFrame({
  templateId,
  title,
  size = 'xl',
  children,
  onClose,
}: {
  templateId: string;
  title: string;
  size?: 'lg' | 'xl';
  children: (template: Template, controls: FrameControls) => ReactNode;
  onClose: () => void;
}) {
  const query = useTemplate(templateId);
  const update = useUpdateTemplate();
  const [name, setName] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const tr = useTr();
  const nameError = useNameError(update.error);
  const template = query.data;
  const currentName = name ?? template?.name ?? '';
  const save = (payload: Record<string, unknown>, found: string[]) => {
    setProblems(found);
    if (!template || found.length > 0 || currentName.trim() === '' || update.isPending) return;
    update.mutate(
      {
        id: template.id,
        ...(currentName.trim() !== template.name && { name: currentName.trim() }),
        payload,
        expected_version: template.version,
      },
      { onSuccess: onClose },
    );
  };
  const formId = 'template-editor-form';
  const { t } = useTranslation();
  return (
    <Dialog
      title={title}
      size={size}
      onClose={onClose}
      footer={
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            disabled={!template || update.isPending}
          >
            {update.isPending ? tr('saving') : t('common.save')}
          </Button>
        </DialogActions>
      }
    >
      {query.isPending && <LoadingState />}
      {query.isError && (
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      )}
      {template && (
        <div className={styles.editor}>
          <TextField
            label={t('templates.name')}
            value={currentName}
            maxLength={100}
            error={nameError}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {children(template, { formId, save, problems })}
          {problems.length > 0 && (
            <ul className={styles.problems} role="alert">
              {problems.map((code) => (
                <li key={code}>{tr(`problems.${code}`)}</li>
              ))}
            </ul>
          )}
          <FormError error={update.error && !nameError ? update.error : null} />
        </div>
      )}
    </Dialog>
  );
}

interface FrameControls {
  formId: string;
  save: (payload: Record<string, unknown>, problems: string[]) => void;
  problems: string[];
}

// --- boards ------------------------------------------------------------------------------------

export function BoardTemplateEditor({
  templateId,
  onClose,
}: {
  templateId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <EditorFrame templateId={templateId} title={t('templates.editor.boardTitle')} onClose={onClose}>
      {(template, controls) => <BoardForm template={template} controls={controls} />}
    </EditorFrame>
  );
}

function BoardForm({ template, controls }: { template: Template; controls: FrameControls }) {
  const { t } = useTranslation();
  const tr = useTr();
  const [form, setForm] = useState<BoardTemplateForm>(() => readBoardTemplate(template.payload));
  const patch = (change: Partial<BoardTemplateForm>) => {
    setForm((current) => ({ ...current, ...change }));
  };
  const moveColumn = (index: number, by: -1 | 1) => {
    const columns = [...form.columns];
    const [item] = columns.splice(index, 1);
    if (item) columns.splice(index + by, 0, item);
    patch({ columns });
  };
  return (
    <form
      id={controls.formId}
      className={styles.editor}
      onSubmit={(event) => {
        event.preventDefault();
        controls.save(applyBoardTemplate(template.payload, form), boardTemplateProblems(form));
      }}
    >
      <p className={styles.note}>
        {tr(form.includeTasks ? 'boardNoteTasks' : 'boardNoteStructure')}
      </p>
      <TextField
        label={tr('boardName')}
        value={form.boardName}
        maxLength={BOARD_NAME_MAX}
        onChange={(event) => {
          patch({ boardName: event.target.value });
        }}
      />
      <TextAreaField
        label={tr('description')}
        value={form.description}
        maxLength={DESCRIPTION_MAX}
        rows={3}
        onChange={(event) => {
          patch({ description: event.target.value });
        }}
      />
      <h3 className={styles.heading}>{tr('columns')}</h3>
      <ul className={styles.rows}>
        {form.columns.map((column, index) => (
          <li key={column.ref} className={styles.editRow}>
            <TextField
              label={tr('columnName')}
              value={column.name}
              maxLength={COLUMN_NAME_MAX}
              onChange={(event) => {
                patch({
                  columns: form.columns.map((c) =>
                    c.ref === column.ref ? { ...c, name: event.target.value } : c,
                  ),
                });
              }}
            />
            <div className={styles.rowTools}>
              <MoveButtons
                index={index}
                count={form.columns.length}
                onMove={(by) => {
                  moveColumn(index, by);
                }}
              />
              <IconButton
                label={
                  column.tasks > 0
                    ? tr('removeColumnWithTasks', { count: column.tasks })
                    : tr('removeColumn')
                }
                icon={<Icons.trash />}
                disabled={form.columns.length <= 1}
                onClick={() => {
                  patch({
                    columns: form.columns.filter((c) => c.ref !== column.ref),
                    tasks: form.tasks.filter((task) => task.column !== column.ref),
                  });
                }}
              />
            </div>
          </li>
        ))}
      </ul>
      <div>
        <Button
          type="button"
          onClick={() => {
            const ref = freshRef('c', [
              ...form.columns.map((c) => c.ref),
              ...form.tasks.map((x) => x.ref),
            ]);
            patch({ columns: [...form.columns, { ref, name: tr('newColumn'), tasks: 0 }] });
          }}
        >
          <Icons.plus /> {tr('addColumn')}
        </Button>
      </div>
      {form.includeTasks && (
        <>
          <h3 className={styles.heading}>{tr('tasks')}</h3>
          {form.tasks.length === 0 && <p className={styles.note}>{tr('noTasks')}</p>}
          <ul className={styles.rows}>
            {form.tasks.map((task) => (
              <li key={task.ref} className={styles.editRow}>
                <TextField
                  label={tr('taskTitle')}
                  value={task.title}
                  maxLength={TASK_TITLE_MAX}
                  onChange={(event) => {
                    patch({
                      tasks: form.tasks.map((x) =>
                        x.ref === task.ref ? { ...x, title: event.target.value } : x,
                      ),
                    });
                  }}
                />
                <label className={styles.inlineField}>
                  <span>{tr('taskColumn')}</span>
                  <select
                    className={styles.select}
                    value={task.column}
                    onChange={(event) => {
                      patch({
                        tasks: form.tasks.map((x) =>
                          x.ref === task.ref ? { ...x, column: event.target.value } : x,
                        ),
                      });
                    }}
                  >
                    {form.columns.map((c) => (
                      <option key={c.ref} value={c.ref}>
                        {c.name || '—'}
                      </option>
                    ))}
                  </select>
                </label>
                <IconButton
                  label={tr('removeTask')}
                  icon={<Icons.trash />}
                  onClick={() => {
                    patch({ tasks: form.tasks.filter((x) => x.ref !== task.ref) });
                  }}
                />
              </li>
            ))}
          </ul>
          <div>
            <Button
              type="button"
              disabled={form.columns.length === 0}
              onClick={() => {
                const first = form.columns[0];
                if (!first) return;
                const ref = freshRef(
                  'k',
                  form.tasks.map((x) => x.ref),
                );
                patch({
                  tasks: [...form.tasks, { ref, column: first.ref, title: tr('newTask') }],
                });
              }}
            >
              <Icons.plus /> {tr('addTask')}
            </Button>
          </div>
        </>
      )}
      <p className={styles.note}>{t('templates.save.omitted.board')}</p>
    </form>
  );
}

// --- databases ---------------------------------------------------------------------------------

export function DatabaseTemplateEditor({
  templateId,
  onClose,
}: {
  templateId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <EditorFrame
      templateId={templateId}
      title={t('templates.editor.databaseTitle')}
      onClose={onClose}
    >
      {(template, controls) => <DatabaseForm template={template} controls={controls} />}
    </EditorFrame>
  );
}

const FIELD_TYPE_OPTIONS = [
  'text',
  'long_text',
  'number',
  'checkbox',
  'date',
  'datetime',
  'single_select',
  'multi_select',
  'status',
  'tags',
  'user',
] as const;

function DatabaseForm({ template, controls }: { template: Template; controls: FrameControls }) {
  const { t } = useTranslation();
  const tr = useTr();
  const [payload, setPayload] = useState<db.Json>(template.payload);
  const [open, setOpen] = useState(0);
  const tables = db.tablesOf(payload);
  const meta = (db.snapshotOf(payload)['database'] ?? {}) as db.Json;
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  const table = tables[open] ?? tables[0];
  const tableIndex = tables[open] ? open : 0;
  const update = (next: db.Json) => {
    setPayload(next);
  };
  return (
    <form
      id={controls.formId}
      className={styles.editor}
      onSubmit={(event) => {
        event.preventDefault();
        controls.save(payload, db.databaseTemplateProblems(payload));
      }}
    >
      <TextField
        label={tr('databaseName')}
        value={text(meta['name'])}
        maxLength={100}
        onChange={(event) => {
          update(
            db.setDatabaseMeta(payload, {
              name: event.target.value,
              description: text(meta['description']),
            }),
          );
        }}
      />
      <TextAreaField
        label={tr('description')}
        value={text(meta['description'])}
        maxLength={5000}
        rows={3}
        onChange={(event) => {
          update(
            db.setDatabaseMeta(payload, {
              name: text(meta['name']),
              description: event.target.value,
            }),
          );
        }}
      />
      <h3 className={styles.heading}>{tr('tables')}</h3>
      <div className={`${styles.tabStrip} scroll-strip`} role="tablist" aria-label={tr('tables')}>
        {tables.map((t2, index) => (
          <button
            key={text(t2['key'])}
            type="button"
            role="tab"
            aria-selected={index === tableIndex}
            className={styles.tabButton}
            onClick={() => {
              setOpen(index);
            }}
          >
            {text(t2['name']) || '—'}
          </button>
        ))}
        <button
          type="button"
          className={styles.tabButton}
          onClick={() => {
            update(db.addTable(payload, tr('newTable')));
            setOpen(tables.length);
          }}
        >
          <Icons.plus /> {tr('addTable')}
        </button>
      </div>
      {table && (
        <TableEditor
          key={text(table['key'])}
          payload={payload}
          index={tableIndex}
          count={tables.length}
          table={table}
          update={update}
          onRemoved={() => {
            setOpen(Math.max(0, tableIndex - 1));
          }}
          onMoved={(to) => {
            setOpen(to);
          }}
          includeRecords={payload['include_records'] === true}
        />
      )}
      <p className={styles.note}>{t('templates.save.omitted.database')}</p>
    </form>
  );
}

function TableEditor({
  payload,
  index,
  count,
  table,
  update,
  onRemoved,
  onMoved,
  includeRecords,
}: {
  payload: db.Json;
  index: number;
  count: number;
  table: db.Json;
  update: (next: db.Json) => void;
  onRemoved: () => void;
  onMoved: (to: number) => void;
  includeRecords: boolean;
}) {
  const { t } = useTranslation();
  const tr = useTr();
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  const columns = db.columnsOf(table);
  const views = db.viewsOf(table);
  const records = db.recordsOf(table);
  return (
    <section className={styles.tableBlock}>
      <div className={styles.editRow}>
        <TextField
          label={tr('tableName')}
          value={text(table['name'])}
          maxLength={100}
          onChange={(event) => {
            update(db.renameTable(payload, index, event.target.value));
          }}
        />
        <div className={styles.rowTools}>
          <MoveButtons
            index={index}
            count={count}
            onMove={(by) => {
              update(db.moveTable(payload, index, by));
              onMoved(index + by);
            }}
          />
          <IconButton
            label={tr('removeTable')}
            icon={<Icons.trash />}
            disabled={count <= 1}
            onClick={() => {
              update(db.removeTable(payload, index));
              onRemoved();
            }}
          />
        </div>
      </div>
      <h4 className={styles.subheading}>{tr('columns')}</h4>
      <ul className={styles.rows}>
        {columns.map((column, ci) => {
          const key = text(column['key']);
          const type = text(column['type']);
          const filled = db.filledCells(table, key);
          return (
            <li key={key} className={styles.columnBlock}>
              <div className={styles.editRow}>
                <TextField
                  label={tr('columnName')}
                  value={text(column['name'])}
                  maxLength={100}
                  onChange={(event) => {
                    update(db.renameColumn(payload, index, key, event.target.value));
                  }}
                />
                <label className={styles.inlineField}>
                  <span>{t('databases.fieldType')}</span>
                  <select
                    className={styles.select}
                    value={type}
                    onChange={(event) => {
                      update(db.changeColumnType(payload, index, key, event.target.value));
                    }}
                  >
                    {FIELD_TYPE_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {t(`databases.types.${value}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className={styles.rowTools}>
                  <MoveButtons
                    index={ci}
                    count={columns.length}
                    onMove={(by) => {
                      update(db.moveColumn(payload, index, ci, by));
                    }}
                  />
                  <IconButton
                    label={tr('removeColumn')}
                    icon={<Icons.trash />}
                    disabled={columns.length <= 1}
                    onClick={() => {
                      update(db.removeColumn(payload, index, key));
                    }}
                  />
                </div>
              </div>
              {includeRecords && filled > 0 && (
                <p className={styles.note}>{tr('columnFilled', { count: filled })}</p>
              )}
              {db.OPTION_TYPES.includes(type) && (
                <ul className={styles.optionList} aria-label={tr('options')}>
                  {db.optionsOf(column).map((option) => (
                    <li key={text(option['key'])} className={styles.optionRow}>
                      <input
                        className={styles.search}
                        value={text(option['label'])}
                        maxLength={100}
                        aria-label={tr('optionLabel')}
                        onChange={(event) => {
                          update(
                            db.renameOption(
                              payload,
                              index,
                              key,
                              text(option['key']),
                              event.target.value,
                            ),
                          );
                        }}
                      />
                      <IconButton
                        label={tr('removeOption')}
                        icon={<Icons.close />}
                        onClick={() => {
                          update(db.removeOption(payload, index, key, text(option['key'])));
                        }}
                      />
                    </li>
                  ))}
                  <li>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        update(db.addOption(payload, index, key, tr('newOption')));
                      }}
                    >
                      <Icons.plus /> {tr('addOption')}
                    </Button>
                  </li>
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <div>
        <Button
          type="button"
          onClick={() => {
            update(db.addColumn(payload, index, tr('newColumn')));
          }}
        >
          <Icons.plus /> {tr('addColumn')}
        </Button>
      </div>
      {views.length > 0 && (
        <>
          <h4 className={styles.subheading}>{tr('views')}</h4>
          <ul className={styles.rows}>
            {views.map((view, vi) => (
              <li key={`${String(vi)}-${text(view['name'])}`} className={styles.editRow}>
                <TextField
                  label={tr('viewName')}
                  value={text(view['name'])}
                  maxLength={100}
                  onChange={(event) => {
                    update(db.renameView(payload, index, vi, event.target.value));
                  }}
                />
                <IconButton
                  label={tr('removeView')}
                  icon={<Icons.trash />}
                  onClick={() => {
                    update(db.removeView(payload, index, vi));
                  }}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {includeRecords && (
        <>
          <h4 className={styles.subheading}>{tr('samples', { count: records.length })}</h4>
          {records.length > 0 && (
            <>
              <ul className={styles.rows}>
                {records.slice(0, 25).map((record, ri) => (
                  <li key={ri} className={styles.editRow}>
                    <span className={styles.sampleText}>{sampleText(record, columns)}</span>
                    <IconButton
                      label={tr('removeSample')}
                      icon={<Icons.trash />}
                      onClick={() => {
                        update(db.removeRecord(payload, index, ri));
                      }}
                    />
                  </li>
                ))}
              </ul>
              {records.length > 25 && (
                <p className={styles.note}>{tr('samplesMore', { count: records.length - 25 })}</p>
              )}
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    update(db.clearRecords(payload, index));
                  }}
                >
                  {tr('clearSamples')}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

/** A one-line look at a sample record: its first filled values. */
function sampleText(record: db.Json, columns: db.Json[]): string {
  const cells = (record['cells'] ?? {}) as db.Json;
  const parts: string[] = [];
  for (const column of columns) {
    const value = cells[String(column['key'])];
    if (value == null || value === '') continue;
    const shown = Array.isArray(value)
      ? value.map((v) => (typeof v === 'string' ? v : '')).join(', ')
      : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : '';
    parts.push(shown.length > 40 ? `${shown.slice(0, 40)}…` : shown);
    if (parts.length === 3) break;
  }
  return parts.join(' · ') || '—';
}

// --- notes -------------------------------------------------------------------------------------

export function DocumentTemplateEditor({
  templateId,
  onClose,
}: {
  templateId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <EditorFrame
      templateId={templateId}
      title={t('templates.editor.documentTitle')}
      onClose={onClose}
    >
      {(template, controls) => <DocumentForm template={template} controls={controls} />}
    </EditorFrame>
  );
}

function DocumentForm({ template, controls }: { template: Template; controls: FrameControls }) {
  const tr = useTr();
  const editor = useRef<TiptapEditor | null>(null);
  const onEditor = useCallback((next: TiptapEditor) => {
    editor.current = next;
  }, []);
  const onChange = useCallback(() => undefined, []);
  const nodes = (template.payload['nodes'] as never[] | undefined) ?? [];
  return (
    <form
      id={controls.formId}
      className={styles.editor}
      onSubmit={(event) => {
        event.preventDefault();
        const content = editor.current?.getJSON().content ?? [];
        controls.save({ ...template.payload, nodes: content }, []);
      }}
    >
      <p className={styles.note}>{tr('documentNote')}</p>
      <StandaloneEditor
        templateId={template.id}
        nodes={nodes}
        onEditor={onEditor}
        onChange={onChange}
      />
    </form>
  );
}
